import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@perxis-auto/instock-sdk/lib/shared/lib/store';
import { selectors as configModelSelectors } from '@perxis-auto/instock-sdk/lib/shared/store/config';
import { EventService, useIsMounted } from '@perxis-auto/shared';
import { useProviderContext as useUIProviderContext } from '@perxis-auto/ui-kit/lib';
import { Loader } from '@perxis-auto/ui-kit/lib/components/Loader';

import { APP_EVENTS } from '@/shared/config';
import { PAGES, PageType } from '@/shared/config/pages';
import { useAppScroll, useCurrentPage } from '@/shared/lib/router';
import { useComparisonActualize } from '@/shared/store/comparison';
import { selectors as configSelectors } from '@/shared/store/config/model';
import { useFavoriteActualize } from '@/shared/store/favorite';

import '@perxis-auto/ui-kit/lib/assets/styles/main.css';

const Car = lazy(() => import('@/pages/car').then(module => ({ default: module.Car })));
const Cars = lazy(() => import('@/pages/cars').then(module => ({ default: module.Cars })));
const Comparison = lazy(() => import('@/pages/comparison').then(module => ({ default: module.Comparison })));
const Favorites = lazy(() => import('@/pages/favorites').then(module => ({ default: module.Favorites })));
const Models = lazy(() => import('@/pages/models').then(module => ({ default: module.Models })));
const Error = lazy(() => import('@/pages/error').then(module => ({ default: module.Error })));

const PAGE_ELEMENTS_MAP: Record<PageType, React.ReactNode> = {
  [PAGES.Cars]: <Cars />,
  [PAGES.Car]: <Car />,
  [PAGES.Comparison]: <Comparison />,
  [PAGES.Favorites]: <Favorites />,
  [PAGES.Models]: <Models />,
  [PAGES[404]]: <Error statusCode={404} />,
  [PAGES[500]]: <Error statusCode={500} />,
};

export function App() {
  const { direction } = useUIProviderContext();

  const isMounted = useIsMounted();

  const [searchParams] = useSearchParams();
  const currentPage = useCurrentPage();

  const { enable: isModelsEnabled } = useAppSelector(configModelSelectors.selectModels);
  const availablePages = useAppSelector(configSelectors.selectAvailablePages);

  const hasQueryParams = searchParams.size > 0;

  const initialRoute = isModelsEnabled ? PAGES.Models : PAGES.Cars;
  const isRedirectInitialRouteRequired = !isMounted.current.mounted && currentPage !== initialRoute && !hasQueryParams;

  useAppScroll();
  useFavoriteActualize();
  useComparisonActualize();

  useEffect(() => {
    EventService.getInstance().sendEvent(APP_EVENTS.Initialized);
  }, []);

  return (
    <div className="perxis-auto-instock" dir={direction}>
      <Suspense fallback={<Loader size="large" className="perxis-auto-instock-loader" />}>
        <Routes>
          {isRedirectInitialRouteRequired && <Route index element={<Navigate to={initialRoute} replace />} />}
          {Object.entries(PAGE_ELEMENTS_MAP).map(([path, element]) => {
            const isAvailable = availablePages[path as keyof typeof availablePages];

            return isAvailable ? <Route key={path} path={path} element={element} /> : null;
          })}
          <Route path="*" element={<Navigate to={PAGES[404]} replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}
